import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Caption, CheckBoxCard, SubHeadingOne } from 'components/core';

import { useRecoilState } from 'recoil';
import { currentVisualizationState, Role, rolesState, visualisationRolesState } from 'states';
import { useRolesService } from 'services';
import styles from './VisualizationTabs.module.css';

interface AccessProps {
  register: any;
  setValue: any;
  formState: any;
  saveAttempted?: boolean;
}

export const Access = ({ saveAttempted = false }: AccessProps) => {
  const { t } = useTranslation();
  const roleService = useRolesService();
  const [roles, setRoles] = useRecoilState(rolesState);
  const [visualisationRoles, setVisualisationRoles] = useRecoilState(visualisationRolesState);
  const [currentVisualization] = useRecoilState(currentVisualizationState);
  const getRoleClickHandler = (role: any) => {
    return () => {
      updateRoles(role);
    };
  };

  useEffect(() => {
    if (!roles.length) {
      getRoles();
    }
  }, []);

  useEffect(() => {
    setVisualisationRoles(currentVisualization.data[0].access.escapedAccessConfig || []);
  }, [currentVisualization]);

  const getRoles = () => {
    roleService.getRoles().then((response) => {
      if (response.length > 0) {
        setRoles(response);
      }
    });
  };

  const isRoleSelected = (roleId: number) => {
    const currentVisualisationRoles: Role[] = [...visualisationRoles];
    return currentVisualisationRoles.findIndex((role) => role.id === roleId) !== -1;
  };

  const updateRoles = (selectedRole: Role) => {
    const currentVisualisationRoles: Role[] = [...visualisationRoles];
    const selectedRoleIndex = currentVisualisationRoles.findIndex(
      (role) => role.id === selectedRole.id
    );
    if (selectedRoleIndex === -1) {
      currentVisualisationRoles.push(selectedRole);
    } else {
      currentVisualisationRoles.splice(selectedRoleIndex, 1);
    }
    setVisualisationRoles(currentVisualisationRoles);
  };

  return (
    <>
      <div className="col-12 mb-3">
        <div className="mt-4">
          <SubHeadingOne title="Access permission" />
        </div>
        <div className="">
          <Caption text="Select roles that require access to this visualization." />
        </div>

        {/* Passive hint — no role selected, before save attempted. Placed
            right under the heading, above the role grid, so it's the first
            thing seen rather than something scrolled past. */}
        {visualisationRoles.length === 0 && !saveAttempted && (
          <div className={`d-flex align-items-center gap-2 mt-3 mb-3 ${styles.accessHint}`} role="note">
            <span className={`material-icons ${styles.accessHintIcon}`}>info</span>
            <span>{t('VALIDATION.ACCESS_ROLE_HINT_VISUALIZATION')}</span>
          </div>
        )}

        {/* Hard error — after failed save with no roles selected */}
        {saveAttempted && visualisationRoles.length === 0 && (
          <div className={`d-flex align-items-center gap-2 mt-3 mb-3 ${styles.accessError}`} role="alert">
            <span className={`material-icons ${styles.accessErrorIcon}`}>error_outline</span>
            <span>{t('VALIDATION.ACCESS_ROLE_REQUIRED_VISUALIZATION')}</span>
          </div>
        )}

        <div className="row">
          {roles.length > 0 &&
            roles.map((role: Role) => {
              if (role.id != 2000) {
                return (
                  <div className="col-sm-12 col-md-4 col-lg-4 mt-3" key={role.id}>
                    <CheckBoxCard
                      label={role.name!}
                      clickHandler={getRoleClickHandler(role)}
                      isChecked={isRoleSelected(role.id!)}
                    />
                  </div>
                );
              }
            })}
          <div className=""></div>
        </div>
      </div>
    </>
  );
};
